<script setup lang="ts">
import { computed, type Ref, useTemplateRef } from 'vue'

import { Card } from '../../'
import type { SegmentCriteriaDto, UserRefDto } from '../../../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import type { GetSegmentCreatorLink, SegmentCreator, SegmentPreviewFilter, UseGetSegmentPreviewReturn } from '../../../types'

import { SEGMENT_PREVIEW_EMPTY_CRITERIA, SEGMENT_PREVIEW_GENERATING, SEGMENT_PREVIEW_TITLE } from './SegmentPreview.helpers'
import SegmentPreviewResults from './SegmentPreviewResults.vue'

const props = withDefaults(
  defineProps<{
    advertiserGroupId?: number
    loadPreview: (filterRef: Ref<SegmentPreviewFilter>) => UseGetSegmentPreviewReturn
    criteria?: SegmentCriteriaDto | null
    manualMembers?: UserRefDto[]
    excludedMembers?: UserRefDto[]
    isRemovable?: boolean
    getCreatorLink?: GetSegmentCreatorLink | null
    pageSize?: number
    segmentId?: string | null
    isGenerating?: boolean
  }>(),
  {
    advertiserGroupId: undefined,
    criteria: null,
    manualMembers: () => [],
    excludedMembers: () => [],
    isRemovable: false,
    getCreatorLink: null,
    pageSize: 10,
    segmentId: null,
    isGenerating: false,
  },
)
const emits = defineEmits<{ remove: [creator: SegmentCreator] }>()

const hasSegmentDefinition = computed(() => !!props.criteria || props.manualMembers.length > 0 || !!props.segmentId)

const results = useTemplateRef('results')

defineExpose({ refetch: () => results.value?.refetch() ?? Promise.resolve() })
</script>

<template>
  <Card v-if="isGenerating" :title="SEGMENT_PREVIEW_TITLE" is-empty :empty-text="SEGMENT_PREVIEW_GENERATING" />

  <SegmentPreviewResults
    v-else-if="hasSegmentDefinition"
    ref="results"
    :advertiser-group-id="advertiserGroupId"
    :load-preview="loadPreview"
    :criteria="criteria"
    :manual-members="manualMembers"
    :excluded-members="excludedMembers"
    :segment-id="segmentId"
    :is-removable="isRemovable"
    :get-creator-link="getCreatorLink"
    :page-size="pageSize"
    @remove="emits('remove', $event)"
  />

  <Card v-else :title="SEGMENT_PREVIEW_TITLE" is-empty :empty-text="SEGMENT_PREVIEW_EMPTY_CRITERIA" />
</template>
